/**
 * AF-136 — competitive `/vs/<rival>` comparison content (HTML publication).
 *
 * Structured source-of-truth for the public `/vs/<rival>` pages. Content is a
 * faithful publication summary of the locked editorial comparison docs at
 * `docs/comparisons/<rival>.md` (each: Tldr + at-a-glance table + cited
 * sources + "when NOT to pick Syncanix"). The markdown remains the canonical
 * long-form; these pages render the Tldr + the most-differentiating
 * at-a-glance rows for SEO + sales.
 *
 * i18n: this file holds only structure + stable `vs.*` translation KEYS plus
 * the verbatim competitor proper name (`rivalName`, never translated). The
 * English source copy lives in the i18n catalog (`src/i18n/messages/en.json`);
 * the `ComparisonPage` / `VsIndex` components resolve every key through
 * `useT()`. The shared Syncanix-cell copy is reused across rivals via shared
 * key constants, exactly as the prose was shared before.
 *
 * Pricing note (BL-044): the Syncanix pricing cell stays neutralised (no
 * contested price numbers) — `vs.cell.syncanixPricing` renders "Published list
 * pricing — paid plans only, no free tier — see /pricing". There is NO free
 * tier under ADR-0149, and no automatic trial since ADR-0489; Starter is the
 * paid entry tier at $299 (the retired Dev tier is gone from every SOT). When
 * BL-044 resolves, the pricing copy can cite the canonical number via that key.
 *
 * ADR-0442 (posted price per interaction) deliberately does NOT change these
 * cells. The comparison table's job is one claim per rival per row, and the
 * neutralised cell already says the only thing that survives a competitor
 * dispute: the prices are published. Naming the 3/6/9/12 menu here would put a
 * fourth copy of it on a page whose own note says to keep numbers off — the
 * menu belongs on /pricing, which this cell links to.
 * Editing rule
 * (`.claude/rules/documentation.md`): when a source doc changes, update its
 * catalog entry in the same change.
 */

export type RivalSlug =
  | 'intercom-fin'
  | 'sierra'
  | 'decagon'
  | 'glean'
  | 'chatbase'
  | 'copilotkit'
  | 'ada'
  | 'zendesk-answer-bot'
  | 'walkme'
  | 'appcues'
  | 'chameleon'
  | 'userpilot';

export interface ComparisonRow {
  /** `vs.*` key for the feature label. */
  readonly featureKey: string;
  /** `vs.*` key for the rival cell. */
  readonly rivalKey: string;
  /** `vs.*` key for the Syncanix cell. */
  readonly syncanixKey: string;
}

/**
 * What KIND of thing the rival is. Four buckets, because the honest answer to
 * "should I read this comparison" is usually "only if you were considering that
 * kind of product" — and two of the eight are not competitors at all.
 *
 * It exists because the `/vs` index rendered eight identical cards: same white
 * ground, same weight, same two-sentence paragraph. A reader had to read all
 * eight to discover that Glean and CopilotKit are adjacent rather than
 * alternatives, which is the single most useful thing the page knows.
 */
export type RivalCategory = 'incumbent' | 'whiteGlove' | 'smb' | 'adjacent' | 'dap';

/**
 * `dap` is the fifth bucket and the one that is NOT a chat product at all.
 *
 * A digital-adoption platform (WalkMe, Appcues, Chameleon, Userpilot) SHOWS a
 * person where to click. That is a different promise from every other rival on
 * this page, and it changes which rows are worth comparing: "can it answer a
 * question" is the wrong axis for a product tour, while "who hand-builds each
 * flow" and "what breaks when you ship" are the two questions anyone who has
 * run one for a year asks first. Hence `vs.feature.authoring` and
 * `vs.feature.maintenance`, which only this bucket uses.
 */

export interface Comparison {
  readonly slug: RivalSlug;
  /** Competitor proper name — verbatim in every locale. */
  readonly rivalName: string;
  readonly category: RivalCategory;
  /**
   * `vs.*` key for the short index-card summary.
   *
   * Retained as the SEO/meta sentence. The index CARD renders `theirsKey` +
   * `verdictKey` instead — the two halves this sentence always had, split so
   * the verdict is scannable rather than buried at the end of a grey
   * paragraph. All three stay in the catalog and are pinned to each other by
   * `comparison-content.test.ts`.
   */
  readonly summaryKey: string;
  /** `vs.*` key for the first half: what the rival is good at. */
  readonly theirsKey: string;
  /** `vs.*` key for the second half: where Syncanix fits instead. */
  readonly verdictKey: string;
  /**
   * `vs.*` key for the doc's Tldr paragraph — the POSITIONING half only.
   *
   * It used to carry the decision rule too, as its last two sentences, which
   * meant the one thing a reader came for ("so which do I pick?") was the tail
   * of a 90-word paragraph. That is the same defect `summaryKey` had and the
   * same fix: the two halves are split out, so the answer is scannable rather
   * than buried. This one was fixed on the index card first and the detail page
   * — where the decision is actually made — was left behind.
   */
  readonly tldrKey: string;
  /** `vs.*` key for "pick the rival if…". Keeps its trailing `→ <rival>`. */
  readonly pickThemKey: string;
  /**
   * `vs.*` key for "pick Syncanix if…". Keeps its trailing `→ Syncanix`.
   *
   * The arrow and the name stay in the CATALOG and are stripped at render.
   * They look redundant under a card already titled "Syncanix" — and they are,
   * visually — but they are the only thing that makes the split checkable in
   * the five locales a reviewer does not read: `comparison-content.test.ts`
   * asserts this half names Syncanix and the other does not, which is exactly
   * how a mechanically-split pair gets caught if it is ever swapped.
   */
  readonly pickUsKey: string;
  readonly rows: readonly ComparisonRow[];
}

// Shared Syncanix-cell keys — reused across rivals (was shared prose before).
const SYNCANIX_DISCOVERY = 'vs.cell.syncanixDiscovery';
const SYNCANIX_DISCOVERY_SELF_SERVE = 'vs.cell.syncanixDiscoverySelfServe';
const SYNCANIX_DISCOVERY_PRODUCT_BACKEND = 'vs.cell.syncanixDiscoveryProductBackend';
const SYNCANIX_WRITE = 'vs.cell.syncanixWrite';
const SYNCANIX_OBS = 'vs.cell.syncanixObs';
const SYNCANIX_RESIDENCY = 'vs.cell.syncanixResidency';
const SYNCANIX_RESIDENCY_SELF_SERVE = 'vs.cell.syncanixResidencySelfServe';
const SYNCANIX_RESIDENCY_HOSTED_PLANE = 'vs.cell.syncanixResidencyHostedPlane';
const SYNCANIX_FREE = 'vs.cell.syncanixFree';
const SYNCANIX_OSS = 'vs.cell.syncanixOss';
const SYNCANIX_OSS_CONTROL_PLANE_CLOSED = 'vs.cell.syncanixOssControlPlaneClosed';
const SYNCANIX_PRICING = 'vs.cell.syncanixPricing';
const SYNCANIX_SEGMENT = 'vs.cell.syncanixSegment';
const SYNCANIX_SEGMENT_CUSTOMER_FACING = 'vs.cell.syncanixSegmentCustomerFacing';

// The four DAP rows Syncanix answers identically for every tour product, because
// the answer is a property of how Syncanix works rather than of who it is being
// compared against. Authored per-rival cells would be four copies of one fact.
const SYNCANIX_AUTHORING = 'vs.cell.syncanixAuthoring';
const SYNCANIX_MAINTENANCE = 'vs.cell.syncanixMaintenance';
const SYNCANIX_WRITE_VS_TOUR = 'vs.cell.syncanixWriteVsTour';
const SYNCANIX_DISCOVERY_VS_TOUR = 'vs.cell.syncanixDiscoveryVsTour';

export const COMPARISONS: Record<RivalSlug, Comparison> = {
  'intercom-fin': {
    slug: 'intercom-fin',
    rivalName: 'Intercom Fin',
    category: 'incumbent',
    summaryKey: 'vs.intercomFin.summary',
    theirsKey: 'vs.intercomFin.theirs',
    verdictKey: 'vs.intercomFin.verdict',
    tldrKey: 'vs.intercomFin.tldr',
    pickThemKey: 'vs.intercomFin.pickThem',
    pickUsKey: 'vs.intercomFin.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.intercomFin.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.intercomFin.row.write.rival',
        syncanixKey: SYNCANIX_WRITE,
      },
      {
        featureKey: 'vs.feature.observability',
        rivalKey: 'vs.intercomFin.row.observability.rival',
        syncanixKey: SYNCANIX_OBS,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.intercomFin.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.intercomFin.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.freeTier',
        rivalKey: 'vs.intercomFin.row.freeTier.rival',
        syncanixKey: SYNCANIX_FREE,
      },
      {
        featureKey: 'vs.feature.openSourceSurface',
        rivalKey: 'vs.intercomFin.row.oss.rival',
        syncanixKey: SYNCANIX_OSS,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.intercomFin.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT,
      },
    ],
  },
  sierra: {
    slug: 'sierra',
    rivalName: 'Sierra',
    category: 'whiteGlove',
    summaryKey: 'vs.sierra.summary',
    theirsKey: 'vs.sierra.theirs',
    verdictKey: 'vs.sierra.verdict',
    tldrKey: 'vs.sierra.tldr',
    pickThemKey: 'vs.sierra.pickThem',
    pickUsKey: 'vs.sierra.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.sierra.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY_SELF_SERVE,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.sierra.row.write.rival',
        syncanixKey: SYNCANIX_WRITE,
      },
      {
        featureKey: 'vs.feature.deployment',
        rivalKey: 'vs.sierra.row.deployment.rival',
        syncanixKey: 'vs.sierra.row.deployment.syncanix',
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.sierra.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY_SELF_SERVE,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.sierra.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.freeTier',
        rivalKey: 'vs.sierra.row.freeTier.rival',
        syncanixKey: SYNCANIX_FREE,
      },
      {
        featureKey: 'vs.feature.openSourceSurface',
        rivalKey: 'vs.sierra.row.oss.rival',
        syncanixKey: SYNCANIX_OSS,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.sierra.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT,
      },
    ],
  },
  decagon: {
    slug: 'decagon',
    rivalName: 'Decagon',
    category: 'whiteGlove',
    summaryKey: 'vs.decagon.summary',
    theirsKey: 'vs.decagon.theirs',
    verdictKey: 'vs.decagon.verdict',
    tldrKey: 'vs.decagon.tldr',
    pickThemKey: 'vs.decagon.pickThem',
    pickUsKey: 'vs.decagon.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.decagon.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.decagon.row.write.rival',
        syncanixKey: SYNCANIX_WRITE,
      },
      {
        featureKey: 'vs.feature.observability',
        rivalKey: 'vs.decagon.row.observability.rival',
        syncanixKey: SYNCANIX_OBS,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.decagon.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY_SELF_SERVE,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.decagon.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.freeTier',
        rivalKey: 'vs.decagon.row.freeTier.rival',
        syncanixKey: SYNCANIX_FREE,
      },
      {
        featureKey: 'vs.feature.openSourceSurface',
        rivalKey: 'vs.decagon.row.oss.rival',
        syncanixKey: SYNCANIX_OSS,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.decagon.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT,
      },
    ],
  },
  glean: {
    slug: 'glean',
    rivalName: 'Glean',
    category: 'adjacent',
    summaryKey: 'vs.glean.summary',
    theirsKey: 'vs.glean.theirs',
    verdictKey: 'vs.glean.verdict',
    tldrKey: 'vs.glean.tldr',
    pickThemKey: 'vs.glean.pickThem',
    pickUsKey: 'vs.glean.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.primarySurface',
        rivalKey: 'vs.glean.row.surface.rival',
        syncanixKey: 'vs.glean.row.surface.syncanix',
      },
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.glean.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY_PRODUCT_BACKEND,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.glean.row.write.rival',
        syncanixKey: SYNCANIX_WRITE,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.glean.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY_SELF_SERVE,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.glean.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.freeTier',
        rivalKey: 'vs.glean.row.freeTier.rival',
        syncanixKey: SYNCANIX_FREE,
      },
      {
        featureKey: 'vs.feature.openSourceSurface',
        rivalKey: 'vs.glean.row.oss.rival',
        syncanixKey: SYNCANIX_OSS,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.glean.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT_CUSTOMER_FACING,
      },
    ],
  },
  chatbase: {
    slug: 'chatbase',
    rivalName: 'Chatbase',
    category: 'smb',
    summaryKey: 'vs.chatbase.summary',
    theirsKey: 'vs.chatbase.theirs',
    verdictKey: 'vs.chatbase.verdict',
    tldrKey: 'vs.chatbase.tldr',
    pickThemKey: 'vs.chatbase.pickThem',
    pickUsKey: 'vs.chatbase.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.chatbase.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.chatbase.row.write.rival',
        syncanixKey: SYNCANIX_WRITE,
      },
      {
        featureKey: 'vs.feature.observability',
        rivalKey: 'vs.chatbase.row.observability.rival',
        syncanixKey: SYNCANIX_OBS,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.chatbase.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY,
      },
      {
        featureKey: 'vs.feature.soc2',
        rivalKey: 'vs.chatbase.row.soc2.rival',
        syncanixKey: 'vs.chatbase.row.soc2.syncanix',
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.chatbase.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.openSourceSurface',
        rivalKey: 'vs.chatbase.row.oss.rival',
        syncanixKey: SYNCANIX_OSS,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.chatbase.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT,
      },
    ],
  },
  copilotkit: {
    slug: 'copilotkit',
    rivalName: 'CopilotKit',
    category: 'adjacent',
    summaryKey: 'vs.copilotkit.summary',
    theirsKey: 'vs.copilotkit.theirs',
    verdictKey: 'vs.copilotkit.verdict',
    tldrKey: 'vs.copilotkit.tldr',
    pickThemKey: 'vs.copilotkit.pickThem',
    pickUsKey: 'vs.copilotkit.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.relationship',
        rivalKey: 'vs.copilotkit.row.relationship.rival',
        syncanixKey: 'vs.copilotkit.row.relationship.syncanix',
      },
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.copilotkit.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY,
      },
      {
        featureKey: 'vs.feature.multiTenantIsolation',
        rivalKey: 'vs.copilotkit.row.isolation.rival',
        syncanixKey: 'vs.copilotkit.row.isolation.syncanix',
      },
      {
        featureKey: 'vs.feature.observability',
        rivalKey: 'vs.copilotkit.row.observability.rival',
        syncanixKey: SYNCANIX_OBS,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.copilotkit.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY_HOSTED_PLANE,
      },
      {
        featureKey: 'vs.feature.openSourceSurface',
        rivalKey: 'vs.copilotkit.row.oss.rival',
        syncanixKey: SYNCANIX_OSS_CONTROL_PLANE_CLOSED,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.copilotkit.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.copilotkit.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT,
      },
    ],
  },
  ada: {
    slug: 'ada',
    rivalName: 'Ada',
    category: 'incumbent',
    summaryKey: 'vs.ada.summary',
    theirsKey: 'vs.ada.theirs',
    verdictKey: 'vs.ada.verdict',
    tldrKey: 'vs.ada.tldr',
    pickThemKey: 'vs.ada.pickThem',
    pickUsKey: 'vs.ada.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.ada.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.ada.row.write.rival',
        syncanixKey: SYNCANIX_WRITE,
      },
      {
        featureKey: 'vs.feature.observability',
        rivalKey: 'vs.ada.row.observability.rival',
        syncanixKey: SYNCANIX_OBS,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.ada.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY_SELF_SERVE,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.ada.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.freeTier',
        rivalKey: 'vs.ada.row.freeTier.rival',
        syncanixKey: SYNCANIX_FREE,
      },
      {
        featureKey: 'vs.feature.openSourceSurface',
        rivalKey: 'vs.ada.row.oss.rival',
        syncanixKey: SYNCANIX_OSS,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.ada.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT,
      },
    ],
  },
  'zendesk-answer-bot': {
    slug: 'zendesk-answer-bot',
    rivalName: 'Zendesk Answer Bot',
    category: 'incumbent',
    summaryKey: 'vs.zendeskAnswerBot.summary',
    theirsKey: 'vs.zendeskAnswerBot.theirs',
    verdictKey: 'vs.zendeskAnswerBot.verdict',
    tldrKey: 'vs.zendeskAnswerBot.tldr',
    pickThemKey: 'vs.zendeskAnswerBot.pickThem',
    pickUsKey: 'vs.zendeskAnswerBot.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.zendeskAnswerBot.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.zendeskAnswerBot.row.write.rival',
        syncanixKey: SYNCANIX_WRITE,
      },
      {
        featureKey: 'vs.feature.channelReach',
        rivalKey: 'vs.zendeskAnswerBot.row.channel.rival',
        syncanixKey: 'vs.zendeskAnswerBot.row.channel.syncanix',
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.zendeskAnswerBot.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.zendeskAnswerBot.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.freeTier',
        rivalKey: 'vs.zendeskAnswerBot.row.freeTier.rival',
        syncanixKey: SYNCANIX_FREE,
      },
      {
        featureKey: 'vs.feature.openSourceSurface',
        rivalKey: 'vs.zendeskAnswerBot.row.oss.rival',
        syncanixKey: SYNCANIX_OSS,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.zendeskAnswerBot.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT,
      },
    ],
  },

  walkme: {
    slug: 'walkme',
    rivalName: 'WalkMe',
    category: 'dap',
    summaryKey: 'vs.walkme.summary',
    theirsKey: 'vs.walkme.theirs',
    verdictKey: 'vs.walkme.verdict',
    tldrKey: 'vs.walkme.tldr',
    pickThemKey: 'vs.walkme.pickThem',
    pickUsKey: 'vs.walkme.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.authoring',
        rivalKey: 'vs.walkme.row.authoring.rival',
        syncanixKey: SYNCANIX_AUTHORING,
      },
      {
        featureKey: 'vs.feature.maintenance',
        rivalKey: 'vs.walkme.row.maintenance.rival',
        syncanixKey: SYNCANIX_MAINTENANCE,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.walkme.row.write.rival',
        syncanixKey: SYNCANIX_WRITE_VS_TOUR,
      },
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.walkme.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY_VS_TOUR,
      },
      {
        featureKey: 'vs.feature.primarySurface',
        rivalKey: 'vs.walkme.row.surface.rival',
        syncanixKey: SYNCANIX_SEGMENT_CUSTOMER_FACING,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.walkme.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.walkme.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY_SELF_SERVE,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.walkme.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT_CUSTOMER_FACING,
      },
    ],
  },
  appcues: {
    slug: 'appcues',
    rivalName: 'Appcues',
    category: 'dap',
    summaryKey: 'vs.appcues.summary',
    theirsKey: 'vs.appcues.theirs',
    verdictKey: 'vs.appcues.verdict',
    tldrKey: 'vs.appcues.tldr',
    pickThemKey: 'vs.appcues.pickThem',
    pickUsKey: 'vs.appcues.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.authoring',
        rivalKey: 'vs.appcues.row.authoring.rival',
        syncanixKey: SYNCANIX_AUTHORING,
      },
      {
        featureKey: 'vs.feature.maintenance',
        rivalKey: 'vs.appcues.row.maintenance.rival',
        syncanixKey: SYNCANIX_MAINTENANCE,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.appcues.row.write.rival',
        syncanixKey: SYNCANIX_WRITE_VS_TOUR,
      },
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.appcues.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY_VS_TOUR,
      },
      {
        featureKey: 'vs.feature.primarySurface',
        rivalKey: 'vs.appcues.row.surface.rival',
        syncanixKey: SYNCANIX_SEGMENT_CUSTOMER_FACING,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.appcues.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.appcues.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY_SELF_SERVE,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.appcues.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT_CUSTOMER_FACING,
      },
    ],
  },
  chameleon: {
    slug: 'chameleon',
    rivalName: 'Chameleon',
    category: 'dap',
    summaryKey: 'vs.chameleon.summary',
    theirsKey: 'vs.chameleon.theirs',
    verdictKey: 'vs.chameleon.verdict',
    tldrKey: 'vs.chameleon.tldr',
    pickThemKey: 'vs.chameleon.pickThem',
    pickUsKey: 'vs.chameleon.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.authoring',
        rivalKey: 'vs.chameleon.row.authoring.rival',
        syncanixKey: SYNCANIX_AUTHORING,
      },
      {
        featureKey: 'vs.feature.maintenance',
        rivalKey: 'vs.chameleon.row.maintenance.rival',
        syncanixKey: SYNCANIX_MAINTENANCE,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.chameleon.row.write.rival',
        syncanixKey: SYNCANIX_WRITE_VS_TOUR,
      },
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.chameleon.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY_VS_TOUR,
      },
      {
        featureKey: 'vs.feature.primarySurface',
        rivalKey: 'vs.chameleon.row.surface.rival',
        syncanixKey: SYNCANIX_SEGMENT_CUSTOMER_FACING,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.chameleon.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.chameleon.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY_SELF_SERVE,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.chameleon.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT_CUSTOMER_FACING,
      },
    ],
  },
  userpilot: {
    slug: 'userpilot',
    rivalName: 'Userpilot',
    category: 'dap',
    summaryKey: 'vs.userpilot.summary',
    theirsKey: 'vs.userpilot.theirs',
    verdictKey: 'vs.userpilot.verdict',
    tldrKey: 'vs.userpilot.tldr',
    pickThemKey: 'vs.userpilot.pickThem',
    pickUsKey: 'vs.userpilot.pickUs',
    rows: [
      {
        featureKey: 'vs.feature.authoring',
        rivalKey: 'vs.userpilot.row.authoring.rival',
        syncanixKey: SYNCANIX_AUTHORING,
      },
      {
        featureKey: 'vs.feature.maintenance',
        rivalKey: 'vs.userpilot.row.maintenance.rival',
        syncanixKey: SYNCANIX_MAINTENANCE,
      },
      {
        featureKey: 'vs.feature.writeActions',
        rivalKey: 'vs.userpilot.row.write.rival',
        syncanixKey: SYNCANIX_WRITE_VS_TOUR,
      },
      {
        featureKey: 'vs.feature.backendApiAutoDiscovery',
        rivalKey: 'vs.userpilot.row.discovery.rival',
        syncanixKey: SYNCANIX_DISCOVERY_VS_TOUR,
      },
      {
        featureKey: 'vs.feature.primarySurface',
        rivalKey: 'vs.userpilot.row.surface.rival',
        syncanixKey: SYNCANIX_SEGMENT_CUSTOMER_FACING,
      },
      {
        featureKey: 'vs.feature.pricingModel',
        rivalKey: 'vs.userpilot.row.pricing.rival',
        syncanixKey: SYNCANIX_PRICING,
      },
      {
        featureKey: 'vs.feature.euDataResidency',
        rivalKey: 'vs.userpilot.row.residency.rival',
        syncanixKey: SYNCANIX_RESIDENCY_SELF_SERVE,
      },
      {
        featureKey: 'vs.feature.targetSegment',
        rivalKey: 'vs.userpilot.row.segment.rival',
        syncanixKey: SYNCANIX_SEGMENT_CUSTOMER_FACING,
      },
    ],
  },
};

/** Stable display order for the index. */
export const COMPARISON_ORDER: readonly RivalSlug[] = [
  'sierra',
  'intercom-fin',
  'decagon',
  'ada',
  'glean',
  'zendesk-answer-bot',
  'chatbase',
  'copilotkit',
  'walkme',
  'appcues',
  'chameleon',
  'userpilot',
];

export function getComparison(slug: string): Comparison | undefined {
  return (COMPARISONS as Record<string, Comparison>)[slug];
}
